import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Sparkles, 
  User, 
  Mail, 
  Phone, 
  CheckCircle2, 
  Loader2, 
  ArrowRight,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";

// Zod validation schema
const contactSchema = z.object({
  nome: z.string().min(2, "Il nome deve avere almeno 2 caratteri"),
  cognome: z.string().min(2, "Il cognome deve avere almeno 2 caratteri"),
  email: z.string().email("Inserisci un indirizzo email valido"),
  telefono: z.string().optional(),
  tipoRichiesta: z.string().min(1, "Seleziona il motivo del contatto"),
  messaggio: z.string().min(2, "Inserisci il tuo messaggio"),
  privacy_consent: z.literal(true, {
    errorMap: () => ({ message: "È necessario accettare l'Informativa sulla Privacy e Cookie Policy" }),
  }),
});

type ContactValues = z.infer<typeof contactSchema>;

export const ContactPopup: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formLoadTime, setFormLoadTime] = useState<number>(0);

  const form = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      nome: "",
      cognome: "",
      email: "",
      telefono: "",
      tipoRichiesta: "",
      messaggio: "",
      privacy_consent: false as any,
    },
  });

  // Auto trigger after 10 seconds only on desktop (once per session)
  useEffect(() => {
    setFormLoadTime(Date.now());
    const hasTriggered = sessionStorage.getItem("forfettari_contact_triggered");
    if (hasTriggered) return;

    // Check if it's desktop (width >= 1024px)
    const isDesktop = window.innerWidth >= 1024;
    if (!isDesktop) return;

    const timer = setTimeout(() => {
      const alreadyTriggered = sessionStorage.getItem("forfettari_contact_triggered");
      if (!alreadyTriggered) {
        setIsOpen(true);
        sessionStorage.setItem("forfettari_contact_triggered", "true");
      }
    }, 10000); // 10 seconds

    return () => clearTimeout(timer);
  }, []);

  const onSubmit = async (data: ContactValues) => {
    setIsSubmitting(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome: data.nome,
          cognome: data.cognome,
          email: data.email,
          telefono: data.telefono,
          tipoRichiesta: data.tipoRichiesta,
          messaggio: data.messaggio,
          privacy_consent: data.privacy_consent,
          formStartTime: formLoadTime,
        }),
      });

      const resData = await response.json().catch(() => ({}));

      if (response.ok && resData.success) {
        setIsSuccess(true);
        toast.success("Richiesta ed email inviate con successo!");
        form.reset();
      } else {
        toast.error(resData.error || "Errore durante l'invio della richiesta.");
      }
    } catch (error) {
      console.error("Contact API Error:", error);
      toast.error("Errore di connessione durante l'invio.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setTimeout(() => setIsSuccess(false), 200);
    }
  };

  return (
    <>
      {/* Floating CTA Button at the bottom-right corner to allow opening manually */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-gradient-to-r from-primary to-indigo-700 text-white px-5 py-3.5 rounded-full shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300 font-semibold group animate-bounce-subtle"
        style={{
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          animation: "float 3s ease-in-out infinite"
        }}
      >
        <HelpCircle className="w-5 h-5 group-hover:rotate-12 transition-transform" />
        <span>Consulenza & Preventivo</span>
      </button>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto p-0 rounded-2xl border bg-card shadow-2xl">
          {/* Header decoration */}
          <div className="h-1.5 w-full bg-gradient-to-r from-primary via-blue-500 to-indigo-600" />
          
          {isSuccess ? (
            <div className="p-8 text-center space-y-6 animate-in fade-in zoom-in-95 duration-300">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-500/10 flex items-center justify-center text-green-600">
                <CheckCircle2 className="w-12 h-12" />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-bold text-green-800">Richiesta ed Email Inviate! ✅</h3>
                <p className="text-muted-foreground text-sm max-w-sm mx-auto leading-relaxed">
                  Grazie per averci contattato. Abbiamo inviato un'email di conferma al tuo indirizzo con il riepilogo della richiesta e ti risponderemo entro 24 ore lavorative.
                </p>
              </div>
              <div className="pt-4">
                <Button onClick={() => handleOpenChange(false)} size="lg" className="w-full sm:w-auto">
                  Chiudi Finestra 👍
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-6 sm:p-8 space-y-6">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold flex items-center gap-2 text-left">
                  <Sparkles className="w-6 h-6 text-primary" />
                  Hai domande sul regime forfettario?
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground text-left mt-1">
                  Chiedi informazioni personalizzate o richiedi direttamente un preventivo per la gestione del tuo regime forfettario.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 text-left">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="nome" className="text-xs font-semibold text-muted-foreground uppercase">Nome *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 h-4.5 w-4.5 text-muted-foreground" />
                      <Input id="nome" placeholder="Mario" className="pl-10" {...form.register("nome")} />
                    </div>
                    {form.formState.errors.nome && (
                      <p className="text-destructive text-xs mt-1">{form.formState.errors.nome.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="cognome" className="text-xs font-semibold text-muted-foreground uppercase">Cognome *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 h-4.5 w-4.5 text-muted-foreground" />
                      <Input id="cognome" placeholder="Rossi" className="pl-10" {...form.register("cognome")} />
                    </div>
                    {form.formState.errors.cognome && (
                      <p className="text-destructive text-xs mt-1">{form.formState.errors.cognome.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs font-semibold text-muted-foreground uppercase">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-4.5 w-4.5 text-muted-foreground" />
                      <Input id="email" type="email" placeholder="mario@example.com" className="pl-10" {...form.register("email")} />
                    </div>
                    {form.formState.errors.email && (
                      <p className="text-destructive text-xs mt-1">{form.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="telefono" className="text-xs font-semibold text-muted-foreground uppercase">Telefono (opzionale)</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-2.5 h-4.5 w-4.5 text-muted-foreground" />
                      <Input id="telefono" type="tel" placeholder="333 1234567 (opzionale)" className="pl-10" {...form.register("telefono")} />
                    </div>
                    {form.formState.errors.telefono && (
                      <p className="text-destructive text-xs mt-1">{form.formState.errors.telefono.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="tipoRichiesta" className="text-xs font-semibold text-muted-foreground uppercase">Cosa desideri richiedere? *</Label>
                  <Select onValueChange={(val) => form.setValue("tipoRichiesta", val)} value={form.watch("tipoRichiesta")}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona un'opzione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="informazioni">Chiedere Informazioni Generali</SelectItem>
                      <SelectItem value="preventivo">Richiedere un Preventivo per Gestione Contabilità</SelectItem>
                      <SelectItem value="apertura">Informazioni Apertura Partita IVA Forfettaria</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.formState.errors.tipoRichiesta && (
                    <p className="text-destructive text-xs mt-1">{form.formState.errors.tipoRichiesta.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="messaggio" className="text-xs font-semibold text-muted-foreground uppercase">Messaggio *</Label>
                  <Textarea id="messaggio" placeholder="Inserisci qui la tua domanda o i dettagli della tua richiesta..." className="min-h-[90px] resize-none" {...form.register("messaggio")} />
                  {form.formState.errors.messaggio && (
                    <p className="text-destructive text-xs mt-1">{form.formState.errors.messaggio.message}</p>
                  )}
                </div>

                {/* GDPR Mandatory Privacy Checkbox */}
                <div className="bg-slate-100 dark:bg-slate-900 p-3.5 rounded-lg border border-slate-300 dark:border-slate-700 my-3">
                  <label htmlFor="popup_privacy_consent" className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="popup_privacy_consent"
                      required
                      className="h-5 w-5 mt-0.5 rounded border-slate-400 text-primary focus:ring-primary cursor-pointer shrink-0"
                      {...form.register("privacy_consent")}
                    />
                    <span className="text-xs text-slate-800 dark:text-slate-200 font-medium leading-relaxed">
                      Ho letto l'informativa sulla <a href="/privacy" target="_blank" className="underline text-primary font-bold hover:opacity-80">Privacy Policy</a> e la <a href="/cookie-policy" target="_blank" className="underline text-primary font-bold hover:opacity-80">Cookie Policy</a> e acconsento al trattamento dei dati personali. *
                    </span>
                  </label>
                  {form.formState.errors.privacy_consent && (
                    <p className="text-destructive text-xs font-semibold mt-1.5">{form.formState.errors.privacy_consent.message}</p>
                  )}
                </div>

                <Button type="submit" className="w-full h-11 text-base font-semibold mt-4" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio in corso...
                    </>
                  ) : (
                    <>
                      Invia Richiesta <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
